import * as React from 'react';
import { observer } from 'mobx-react';
import { PagePaymentSuccessViewModel } from './page-payment-success-view-model';
import { GoogleAnalyticsWrapper } from '../../infrastructure/google-analytics-wrapper';
import { getNewRelicBrowserAgent } from '../../infrastructure/new-relic-browser-agent';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { Button, ButtonStyle } from '../../primitives/button/button';
import { ButtonLink } from '../../primitives/button/button-link';
import { ErrorMessage } from '../../components/error-message/error-message';
import { FadeTransition, fadeTransitionDuration } from '../../components/fade-transition';
import { PanelContentTransition } from '../../components/panel-content-transition';
import { FocusOnMount } from '../../components/focus-on-mount-hoc';
import { AppStoreCallToAction } from '../../components/app-store-call-to-action/app-store-call-to-action';
import { getPageClasses } from '../helpers/get-page-classes';
import { GiftComplete } from '../../components/gift/gift-complete';
import { PanelHeading } from '../../components/panel-heading';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { PanelFooter } from '../../components/panel-footer';
import { pageFooter } from '../../main/webgiving-main.less';
import { HeartAnimation } from '../../components/heart-animation/heart-animation';
import * as styles from './page-payment-success.less';
import { frequencyLabelSentenceCase } from '../../utils/recurring-utils';
import { Formatter } from '../../../Shared/utils/formatter';
import { Text } from '../../components/text';
import { Switch } from '../../components/switch';
import { StandardTooltip, TooltipPlacement } from '../../components/tooltip/tooltip';
import { RecurringSuggestionButton } from '../../components/recurring-suggestion-button';
export { PagePaymentSuccessViewModel };

@injectWebGivingProps
@observer
export class PagePaymentSuccess extends React.Component<{
	vm: PagePaymentSuccessViewModel,
	transitionState: string,
} & InjectableWebGivingProps> {
	panelBodyNode: HTMLDivElement;

	render() {
		const { vm, transitionState } = this.props;
		const {
			pageShouldBeVisible,
			error,
			data: {
				successThankYouMessage,
				getAppUrl,
				paymentLabel,
				merchantAppName,
				isRecurring,
				stillProcessing,
				wasDelayed,
				giftAmount,
				fundName,
				emailAddress,
				receiptSent,
				merchantCurrency,
				merchantName,
				recurringStartDate,
				recurringFrequency,
				hasCustomApp,
				hasWelcomeSmsBeenSent,
				hideAppCallToAction,
				recurringInfo,
				appContentCard,
				isOneTimeFutureGift,
				oneTimeGiftDate,
				fastModeAvailable,
				fastModeDailyLimit,
				thankYouText,
				thankYouTextHtml,
				returnButtonText,
				returnButtonUrl,
				splitLineItemResults
			},
			isFastModeOn,
			isFastModeLoading
		} = vm;

		const elementsShouldEnter = transitionState === 'entered';
		const shouldShowCallToAction = !hideAppCallToAction && hasCustomApp;
		const hasUiConfigReturnOverride = !!returnButtonUrl && !!returnButtonText;

		return (
			<div className={getPageClasses(pageShouldBeVisible, false)} data-pp-at-target={pageShouldBeVisible ? 'Payment Success page' : null}>
				<PanelHeading vm={vm}>
					<FadeTransition in={elementsShouldEnter}>
						<div>
							<ErrorMessage error={error} />
							<HeartAnimation onMount={this.handleAnimation} />
							<GiftComplete
								emailAddress={emailAddress}
								fundName={fundName}
								giftAmount={giftAmount}
								isRecurring={isRecurring}
								receiptSent={receiptSent}
								merchantCurrency={merchantCurrency}
								merchantName={merchantName}
								paymentLabel={paymentLabel}
								stillProcessing={stillProcessing}
								wasDelayed={wasDelayed}
								startDate={recurringStartDate}
								frequency={recurringFrequency}
								displayRecurringSuggestion={!!recurringInfo && recurringInfo.ShowSuggestion}
								isOneTimeFutureGift={isOneTimeFutureGift}
								oneTimeGiftDate={oneTimeGiftDate}
								thankYouText={thankYouText}
								thankYouTextHtml={thankYouTextHtml}
								splitLineItemResults={splitLineItemResults}
							/>
							{fastModeAvailable && (
								<div className={styles.fastModeWrap}>
									<div className={styles.fastModeToggleWrap}>
										<h2 className={styles.fastModeTitle} data-pp-at-target="fast-mode-enable-title">
											<Text>ENABLE FASTMODE</Text>
										</h2>
										<StandardTooltip
											placement={TooltipPlacement.Top}
											content={<Text>{`By enabling Fastmode, you'll be able to give (up to double of your average/highest ${paymentLabel.NounLowerCase} from the last 90 days) without a confirmation code. To receive a confirmation code each time, turn Fastmode off.`}</Text>}
											tooltipContentId="fast-mode-enable-tooltip"
										/>
										<Switch
											className={styles.fastModeSwitch}
											disabled={isFastModeLoading}
											value={isFastModeOn}
											data-pp-at-target="fast-mode-enable-switch"
											onChangeHandler={this.updateFastMode}
										/>
									</div>
									{!!fastModeDailyLimit && <p className={styles.fastModelimit}><Text>Fastmode daily limit is </Text><strong>{merchantCurrency} {`$${Formatter.formatNumberForDisplay(fastModeDailyLimit, 2)}`}</strong></p>}
								</div>
							)}
							<p>
								<FocusOnMount delay={fadeTransitionDuration}>
									{hasUiConfigReturnOverride ? (
										<ButtonLink
											href={returnButtonUrl}
											className="button"
											buttonStyle={ButtonStyle.Ghost}
											acceptanceTestTargetId="return button"
										>
											<Text>{returnButtonText}</Text>
										</ButtonLink>
									) : (
										this.giveAgainOrRecurring(vm)
									)}
								</FocusOnMount>
							</p>
						</div>
					</FadeTransition>
				</PanelHeading>
				{(successThankYouMessage) && (<div className="panel-body" ref={this.savePanelBodyNode}>
					<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
						<div>
							<div className={styles.customSuccessThankYouMessage} dangerouslySetInnerHTML={{ __html: successThankYouMessage as string }}></div>
						</div>
					</PanelContentTransition>
				</div>)}
				{shouldShowCallToAction &&
					<PanelFooter className={pageFooter}>
						<AppStoreCallToAction getAppUrl={getAppUrl}
							mobileAppName={merchantAppName}
							paymentLabel={paymentLabel}
							hasCustomApp={hasCustomApp}
							hasWelcomeSmsBeenSent={hasWelcomeSmsBeenSent}
							appContentCard={appContentCard}
						/>
					</PanelFooter>}
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private giveAgainOrRecurring = (vm: PagePaymentSuccessViewModel) => {
		const {
			isLoading,
			data: {
				recurringInfo,
				paymentLabel,
			},
		} = vm;

		return recurringInfo &&
			recurringInfo.Frequency &&
			frequencyLabelSentenceCase(recurringInfo.Frequency) &&
			recurringInfo.ShowSuggestion ? (
			<RecurringSuggestionButton
				acceptanceTestTargetId="Schedule recurring"
				onClick={this.setRecurring}
				isLoading={isLoading}
				paymentLabel={paymentLabel}
				frequency={recurringInfo.Frequency}
				amount={recurringInfo.Amount}
			/>
		) : (
			<Button
				className="button"
				onClick={this.giveAgain}
				buttonStyle={ButtonStyle.Ghost}
				acceptanceTestTargetId="Give again"
				isLoading={isLoading}
			>
				<Text>{`${paymentLabel.VerbSentenceCase} again`}</Text>
			</Button>
		);
	}


	private giveAgain = () => {
		GoogleAnalyticsWrapper.sendEvent('WebGiving Page View', 'giveAgain');
		getNewRelicBrowserAgent().setCurrentRouteName(`${window.location.pathname}/GiveAgain`);
		raiseAction(new WebGivingUserAction.GiveAgain(), this.props);

		this.clearPreconfiguredUrl();
		this.handleNext();
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private setRecurring = () => {
		this.clearPreconfiguredUrl();
		GoogleAnalyticsWrapper.sendEvent('WebGiving Page View', 'setRecurringSuggestion');
		getNewRelicBrowserAgent().addPageAction('setRecurringSuggestion');
		raiseAction(new WebGivingUserAction.SetRecurringSuggestion(), this.props);

		this.handleNext();
	}

	private clearPreconfiguredUrl = () => {
		const query = window.location.search.substring(1);
		if (query.length) {
			if (NewFeatures.AddNewRelicBrowserToWebGiving) {
				if (window.history && window.history.replaceState) {
					window.history.replaceState({}, document.title, window.location.pathname);
				}
			} else {
				if (window.history && window.history.pushState) {
					window.history.pushState({}, document.title, window.location.pathname);
				}
			}			
		}
	}

	private updateFastMode = (mode: boolean) => {
		this.props.vm.setFastModeLoading(true);
		raiseAction(new WebGivingUserAction.UpdateFastModeEnabled(mode), this.props);
	}

	private handleAnimation = () => {
		raiseAction(new WebGivingUserAction.AnimateThankYou(this.props.vm.data.thankYouAnimation), this.props);
	}

	private handleNext = () => {
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	}
}
